import { useState } from "react";
import type { DemoBundle, Step, TextCard } from "../../../shared/types";
import { api, uploadMediaFile } from "../../api";
import { useEditor } from "../DemoEditor";

// Video upload is off (storage): images only. Videos already in a demo still play.
const ACCEPT = "image/png,image/jpeg,image/webp,image/gif";
export { ACCEPT as MEDIA_ACCEPT };

/**
 * Everything that adds, moves or removes screens in the current path (pathId null = the default path): upload, add a
 * capture or text card, copy a screen from another path, reorder, duplicate, replace media, delete. Every path's steps
 * are its own: all of these change this path only. Each call refreshes the bundle. `onAdded` gets the id of the first
 * screen a call created, so the studio can select it.
 */
export function useStepActions(pathId: string | null, seq: Step[], onAdded: (id: string) => void) {
  const { bundle, setBundle } = useEditor();
  const [progress, setProgress] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const firstNew = (before: DemoBundle, after: DemoBundle) => {
    const had = new Set(before.steps.map((s) => s.id));
    return after.steps.find((s) => !had.has(s.id))?.id;
  };

  const act = async (fn: () => Promise<DemoBundle>) => {
    setError(null);
    try { const b = await fn(); setBundle(b); return b; } catch (e: any) { setError(e.message); return null; }
  };

  const upload = async (files: FileList | File[] | null) => {
    if (!files || files.length === 0) return;
    const list = [...files].filter((f) => f.type.startsWith("image/"))
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
    if (list.length === 0) { setError("Those files aren't images. Use PNG, JPEG, WebP or GIF (video upload is turned off)."); return; }
    setError(null);
    let latest = bundle;
    try {
      for (let i = 0; i < list.length; i++) {
        setProgress(`Uploading ${i + 1} of ${list.length}: ${list[i].name}`);
        const media = await uploadMediaFile(list[i]);
        latest = await api.addStep(bundle.demo.id, { outcomeId: pathId, imageKey: media.key, imageWidth: media.width, imageHeight: media.height, mediaType: media.mediaType });
      }
      setBundle(latest);
      const id = firstNew(bundle, latest);
      if (id) onAdded(id);
    } catch (e: any) {
      setError(e.message);
      setBundle(await api.demo(bundle.demo.id));
    } finally {
      setProgress(null);
    }
  };

  const addCapture = async (c: { screenshotKey: string; width: number; height: number; htmlKey: string }) => {
    const b = await act(() => api.addStep(bundle.demo.id, { outcomeId: pathId, imageKey: c.screenshotKey, imageWidth: c.width, imageHeight: c.height, captureKey: c.htmlKey }));
    const id = b && firstNew(bundle, b);
    if (id) onAdded(id);
  };

  const addTextCard = async (role: "intro" | "outro" | "card") => {
    const defaultTitle =
      role === "intro"
        ? `Welcome to ${bundle.demo.title}`
        : role === "outro"
        ? "You're all set!"
        : "Overview";
    const defaultBody =
      role === "intro"
        ? "Take a quick interactive walkthrough to explore how it works."
        : role === "outro"
        ? "Thanks for watching! Ready to take the next step?"
        : "Here are the key takeaways for this section.";
    const defaultBadge =
      role === "intro"
        ? "Interactive Tour"
        : role === "outro"
        ? "Next Steps"
        : "Notice";
    const defaultButton =
      role === "intro"
        ? "Start Tour"
        : role === "outro"
        ? (bundle.demo.ctaLabel || "Finish")
        : "Next";

    const textCard: TextCard = {
      role,
      badge: defaultBadge,
      title: defaultTitle,
      body: defaultBody,
      buttonLabel: defaultButton,
      buttonUrl: role === "outro" ? (bundle.demo.ctaUrl || "") : "",
      secondaryLabel: role === "outro" ? "Watch again" : "",
      style: "default",
    };

    const b = await act(() =>
      api.addStep(bundle.demo.id, {
        outcomeId: pathId,
        textCard,
        title: defaultTitle,
        subtitle: role === "intro" ? "Intro screen" : role === "outro" ? "Outro screen" : "Text card",
      })
    );
    const id = b && firstNew(bundle, b);
    if (!b || !id) return;

    if (role === "intro" && seq.length > 0) {
      await act(() => api.setPathSteps(bundle.demo.id, pathId, [id, ...seq.map((s) => s.id)]));
    }
    onAdded(id);
  };

  /** New order for this path, as ids. Returns false (and reports) if the server refused it. */
  const reorder = async (ids: string[]) => {
    const b = await act(() => api.setPathSteps(bundle.demo.id, pathId, ids));
    return !!b;
  };

  /** A copy (a screen of its own) right after the original, on this path only. */
  const duplicate = async (s: Step) => {
    const b = await act(() => api.cloneStep(s.id, pathId));
    const id = b && firstNew(bundle, b);
    if (id) onAdded(id);
  };

  /** Deletes the step from this path. Other paths' steps for the same screen are their own and stay. */
  const remove = async (s: Step, index: number) => {
    const here = pathId ? `"${bundle.outcomes.find((o) => o.id === pathId)?.name ?? "this path"}"` : "the default path";
    if (!confirm(`Delete screen ${index + 1} from ${here}? Other paths keep their own copies.`)) return false;
    return !!(await act(() => api.deleteStep(s.id)));
  };

  /** Copies a step from another path to the end of this one: the same media, with its edits, as a step of its own. */
  const include = async (id: string) => {
    setError(null);
    try {
      const r = await api.copyToPath(bundle.demo.id, pathId, [id]);
      setBundle(r.bundle);
      if (r.added[0]) onAdded(r.added[0]);
    } catch (e: any) { setError(e.message); }
  };

  const replace = async (s: Step, f: File) => {
    setProgress("Replacing…");
    try {
      const media = await uploadMediaFile(f);
      await act(() => api.updateStep(s.id, { imageKey: media.key, imageWidth: media.width, imageHeight: media.height, mediaType: media.mediaType, captureKey: null }));
    } catch (e: any) { setError(e.message); } finally { setProgress(null); }
  };

  return { progress, error, setError, upload, addCapture, addTextCard, reorder, duplicate, remove, include, replace };
}
export type StepActions = ReturnType<typeof useStepActions>;
